import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import TableComponent from '../../Components/TableComponent/TableComponent';
import { SnackBarComponent } from '../../Components/snackBar/snackBar';
import { LoadingSpinner } from '../../Components/spinner/spinner';
import { API_BASE_URL } from '../../config';
import api from '../../api/axiosInterceptor';
import axios from 'axios';

export const AdminHeadOfficeComponent = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedHeadOffice, setSelectedHeadOffice] = useState(null);

  const [formData, setFormData] = useState({
    designation: '',
    ph_number: '',
    email: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteHeadOffice, setDeleteHeadOffice] = useState(null);

  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl no.', width: 70 },
    { id: 'designation', field: 'designation', headerName: 'Address', width: 200 },
    { id: 'phoneNumber', field: 'phoneNumber', headerName: 'Phone Number', width: 150 },
    { id: 'emailId', field: 'emailId', headerName: 'Email Id', width: 250 },
    {
      id: 'actions',
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)}>
            <Edit />
          </IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)}>
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  useEffect(() => {
    fetchHeadOffices();
  }, []);

  const fetchHeadOffices = () => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/home-notification/dep-head-offices/`)
      .then(response => {
        const apiData = response.data.map((item, index) => ({
          id: item.id || index,
          slNo: index + 1,
          designation: item.designation,
          phoneNumber: item.ph_number,
          emailId: item.email,
        }));
        setRows(apiData);
        setLoading(false);
      })
      .catch(error => {
        console.error('Failed to fetch Head Office contacts:', error);
        setSnackbarMessage('Failed to load Head Office contacts.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      });
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleAddHeadOffice = () => {
    setIsEditMode(false);
    setFormData({ designation: '', ph_number: '', email: '' });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleEdit = (headOffice) => {
    setIsEditMode(true);
    setSelectedHeadOffice(headOffice);
    setFormData({
      designation: headOffice.designation,
      ph_number: headOffice.phoneNumber,
      email: headOffice.emailId,
    });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleDeleteConfirmation = (headOffice) => {
    setDeleteHeadOffice(headOffice);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    api.delete(`/home-notification/dep-head-offices/${deleteHeadOffice.id}/`)
      .then(() => {
        setSnackbarMessage('Head Office contact deleted successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchHeadOffices();
      })
      .catch(error => {
        console.error('Delete failed:', error);
        setSnackbarMessage('Failed to delete Head Office contact.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setDeleteHeadOffice(null);
      });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.designation?.trim()) errors.designation = 'Adress is required';
    if (!formData.ph_number?.trim()) errors.ph_number = 'Phone number is required';
    if (!formData.email?.trim()) errors.email = 'Email is required';
    // else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Invalid email format';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = () => {
    if (!validateForm()) return;
    if (isEditMode) {
      api.patch(`/home-notification/dep-head-offices/${selectedHeadOffice.id}/`, formData)
        .then(() => {
          setSnackbarMessage('Head Office contact updated successfully.');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
          fetchHeadOffices();
        })
        .catch(error => {
          console.error('Update failed:', error);
          setSnackbarMessage('Failed to update Head Office contact.');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        })
        .finally(() => setOpenFormDialog(false));
    } else {
      api.post(`/home-notification/dep-head-offices/`, formData)
        .then(() => {
          setSnackbarMessage('Head Office contact added successfully.');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
          fetchHeadOffices();
        })
        .catch(error => {
          console.error('Add failed:', error);
          setSnackbarMessage('Failed to add Head Office contact.');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        })
        .finally(() => setOpenFormDialog(false));
    }
  };

  if (loading) {
    return <LoadingSpinner size={30} />;
  }

  return (
    <Box
      sx={{
        width: '100%',
        mt: 8,
        mb: 1,
        borderRadius: 2,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        backgroundColor: '#fff',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" backgroundColor="rgba(195, 234, 203, 0.1)" p={2}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Department Head Office Contacts
        </Typography>
        <Button variant="contained" color="primary" onClick={handleAddHeadOffice}>
          Add Contact
        </Button>
      </Box>

      <TableComponent
        columns={columns}
        rows={rows}
        manageColumnStatus
        enableSort={true}
        sx={{
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#1976d2',
            color: '#fff',
            fontWeight: 'bold',
          },
        }}
      />

      <Dialog open={openFormDialog} onClose={() => setOpenFormDialog(false)}>
        <DialogTitle>{isEditMode ? 'Edit Head Office Contact' : 'Add Head Office Contact'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Address"
            fullWidth
            value={formData.designation}
            error={!!formErrors.designation}
            helperText={formErrors.designation}
            onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Phone Number"
            fullWidth
            value={formData.ph_number}
            error={!!formErrors.ph_number}
            helperText={formErrors.ph_number}
            onChange={(e) => setFormData({ ...formData, ph_number: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Email Id"
            fullWidth
            value={formData.email}
            error={!!formErrors.email}
            helperText={formErrors.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFormDialog(false)}>Cancel</Button>
          <Button onClick={handleFormSubmit} variant="contained" color="primary">
            {isEditMode ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this Head Office contact?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <SnackBarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};
